import { ref, computed, watch } from "vue";
import api from "@/api";
import { currentStation, userInfos } from "@/state/api/userState";
import useAccess from "@/composable/useAccess";

/**
 * Session de caisse — état réactif partagé pour le badge header,
 * les modaux, et la page Ma Caisse.
 *
 * Le state est module-level (singleton) : changer une session
 * (ouverture / clôture) dans un composant met à jour tous les
 * consommateurs sans prop drilling.
 */

// ─── State singleton ──────────────────────────────────────────────
const currentSession = ref<any | null>(null);
const currentBreakdown = ref<any | null>(null);
const isLoading = ref(false);
const lastError = ref<string | null>(null);

// Empêche les fetches en rafale (changement d'agence + watcher
// userInfos qui se déclenchent simultanément au boot).
let inflight: Promise<void> | null = null;

const isOpen = computed(() => currentSession.value?.status === "open");

const hasCashierRole = () =>
	useAccess("visibility", [
		"PassengerCashier",
		"ParcelCashier",
		"MainCashier",
		"Admin",
		"SecondAdmin",
	]);

/**
 * Fetch la session ouverte du user sur l'agence courante. Idempotent —
 * on peut l'appeler à chaque navigation sans surcoût (cache appliqué
 * côté axios pour /current).
 */
async function refresh(): Promise<void> {
	const station = currentStation.value;
	if (!station?.key || station.key === -1) {
		// Vue d'ensemble (Admin sur dashboard) : pas de notion de
		// caisse rattachée — on vide.
		currentSession.value = null;
		currentBreakdown.value = null;
		return;
	}

	// Pas de fetch pour rôles qui n'ont pas la perm — évite le 403
	// inutile + bruit dans la console.
	if (!hasCashierRole()) {
		currentSession.value = null;
		currentBreakdown.value = null;
		return;
	}

	if (inflight) return inflight;

	inflight = (async () => {
		isLoading.value = true;
		lastError.value = null;
		try {
			const r: any = await api().cashierSession.current(station.key);
			const data = r?.data;
			// L'API renvoie `null` quand aucune session ouverte (200 OK),
			// ou un objet { session, breakdown } quand il y en a une.
			if (data && data.session) {
				currentSession.value = data.session;
				currentBreakdown.value = data.breakdown;
			} else {
				currentSession.value = null;
				currentBreakdown.value = null;
			}
		} catch (err: any) {
			lastError.value = err?.data?.response?.message || err?.statusText || "Erreur de chargement";
			currentSession.value = null;
			currentBreakdown.value = null;
		}
		isLoading.value = false;
		inflight = null;
	})();

	return inflight;
}

async function openSession(opts: { opening_float: number; notes?: string }): Promise<any> {
	const station = currentStation.value;
	if (!station?.key) throw new Error("Aucune agence sélectionnée");
	const r: any = await api().cashierSession.open({
		station_id: station.key,
		opening_float: opts.opening_float,
		notes: opts.notes,
	});
	await refresh();
	return r;
}

async function closeSession(
	uuid: string,
	opts: { declared_total: number; notes?: string }
): Promise<any> {
	const r: any = await api().cashierSession.close(uuid, opts);
	await refresh();
	return r;
}

/**
 * Déclenche le téléchargement du PDF dans le navigateur. Fetch en blob
 * pour inclure l'Authorization Bearer header, puis crée un object URL
 * et un <a download> programmatique.
 */
async function downloadPdf(uuid: string, suggestedName?: string): Promise<void> {
	const r: any = await api().cashierSession.downloadPdf(uuid);
	// Selon le client HTTP, `r` peut être la Blob directement ou un objet
	// enveloppé. callApi.ts résout response.data → on récupère le blob.
	const blob = r instanceof Blob ? r : new Blob([r], { type: "application/pdf" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = suggestedName ?? `cashier-session-${uuid.slice(0, 8)}.pdf`;
	document.body.appendChild(a);
	a.click();
	a.remove();
	URL.revokeObjectURL(url);
}

// Re-fetch dès qu'on change d'agence ou que le user change.
watch([() => currentStation.value?.key, () => userInfos.value?.account?.id], () => {
	refresh().catch(() => {}); // catch silent (déjà loggé)
});

export default function useCashierSession() {
	return {
		currentSession,
		currentBreakdown,
		isOpen,
		isLoading,
		lastError,
		refresh,
		openSession,
		closeSession,
		downloadPdf,
	};
}
